import { useEffect, useCallback } from 'react';
import {
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
  HiOutlineXCircle,
  HiOutlineInformationCircle,
  HiOutlineQuestionMarkCircle,
  HiOutlineX
} from 'react-icons/hi';


export type ModalType = 'success' | 'error' | 'warning' | 'info' | 'confirm' | 'delete';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void | Promise<void>;
  type?: ModalType;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
  loading?: boolean;
}

function Modal({
  isOpen,
  onClose,
  onConfirm,
  type = 'info',
  title,
  message,
  confirmText = 'Aceptar',
  cancelText = 'Cancelar',
  showCancel = false,
  loading = false
}: ModalProps): JSX.Element | null {

  
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && !loading) {
      onClose();
    }
  }, [onClose, loading]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const getIconAndColor = () => {
    switch (type) {
      case 'success':
        return {
          icon: <HiOutlineCheckCircle />,
          color: 'var(--secondary)',
          bgColor: 'rgba(122, 185, 0, 0.12)'
        };
      case 'error':
        return {
          icon: <HiOutlineXCircle />,
          color: 'var(--accent-pink)',
          bgColor: 'rgba(255, 107, 157, 0.12)'
        };
      case 'warning':
        return {
          icon: <HiOutlineExclamationCircle />,
          color: 'var(--accent-orange)',
          bgColor: 'rgba(255, 159, 67, 0.12)'
        };
      case 'confirm':
        return {
          icon: <HiOutlineQuestionMarkCircle />,
          color: 'var(--primary)',
          bgColor: 'rgba(101, 45, 134, 0.12)'
        };
      case 'delete':
        return {
          icon: <HiOutlineExclamationCircle />,
          color: 'var(--accent-pink)',
          bgColor: 'rgba(255, 107, 157, 0.12)'
        };
      default:
        return {
          icon: <HiOutlineInformationCircle />,
          color: 'var(--accent-cyan)',
          bgColor: 'rgba(0, 212, 255, 0.12)'
        };
    }
  };

  const { icon, color, bgColor } = getIconAndColor();

  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm();
    }
    onClose();
  };

  const getDefaultTitle = () => {
    switch (type) {
      case 'success': return '¡Éxito!';
      case 'error': return 'Error';
      case 'warning': return 'Advertencia';
      case 'confirm': return 'Confirmar';
      case 'delete': return '¿Eliminar?';
      default: return 'Información';
    }
  };

  const shouldShowCancel = showCancel || type === 'confirm' || type === 'delete';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1rem',
        animation: 'fadeIn 0.2s ease'
      }}
      onClick={() => !loading && onClose()}
    >
      <div
        style={{
          background: 'var(--bg-card)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-xl)',
          width: '100%',
          maxWidth: '400px',
          overflow: 'hidden',
          animation: 'slideUp 0.3s ease'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        
        <button
          onClick={onClose}
          disabled={loading}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'none',
            border: 'none',
            padding: '8px',
            cursor: loading ? 'not-allowed' : 'pointer',
            color: 'var(--text-muted)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            opacity: loading ? 0.5 : 1
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.background = 'var(--bg-hover)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'none';
            e.currentTarget.style.color = 'var(--text-muted)';
          }}
        >
          <HiOutlineX style={{ fontSize: '1.25rem' }} />
        </button>

        
        <div style={{ padding: '2rem', textAlign: 'center', position: 'relative' }}>
       
          <div
            style={{
              width: '72px',
              height: '72px',
              borderRadius: 'var(--radius-full)',
              background: bgColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.25rem',
              color: color,
              fontSize: '2.5rem',
              animation: type === 'success' ? 'bounceIn 0.5s ease' : 'none'
            }}
          >
            {icon}
          </div>

         
          <h3
            style={{
              margin: '0 0 0.75rem',
              fontSize: '1.25rem',
              fontWeight: 700,
              color: 'var(--text-primary)'
            }}
          >
            {title || getDefaultTitle()}
          </h3>

         
          {message && (
            <p
              style={{
                margin: 0,
                fontSize: '0.9375rem',
                color: 'var(--text-secondary)',
                lineHeight: 1.6
              }}
            >
              {message}
            </p>
          )}
        </div>

        
        <div
          style={{
            padding: '0 2rem 2rem',
            display: 'flex',
            gap: '0.75rem',
            justifyContent: 'center'
          }}
        >
          {shouldShowCancel && (
            <button
              onClick={onClose}
              disabled={loading}
              className="btn btn-outline"
              style={{
                flex: 1,
                maxWidth: '140px',
                opacity: loading ? 0.5 : 1,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={() => void handleConfirm()}
            disabled={loading}
            className={`btn ${type === 'delete' ? 'btn-danger' : 'btn-primary'}`}
            style={{
              flex: 1,
              maxWidth: shouldShowCancel ? '140px' : '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? (
              <>
                <span className="spinner" style={{ width: '16px', height: '16px' }} />
                Procesando...
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>

      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes bounceIn {
          0% { transform: scale(0.5); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
        .btn-danger {
          background: var(--accent-pink) !important;
          border-color: var(--accent-pink) !important;
          color: white !important;
        }
        .btn-danger:hover {
          background: #e55a85 !important;
          border-color: #e55a85 !important;
        }
      `}</style>
    </div>
  );
}

export default Modal;